import type { BadgeProps } from '@chakra-ui/react'

/**
 * Your-turn counts for the Advisors chrome (W3, plan L20–L27).
 *
 * Leaves are items the viewer owes a response on (agreements, invites, applications, sessions) or
 * unread comments (Messages, session chat). Parents add the leaves they contain. Inventory counts
 * (`countGeneric`) never feed these numbers.
 */
export type AdvisorsAttention = {
  agreementsWaitingOnYou: number
  boardInvites: number
  unreviewedApplications: number
  sessionsNeedingAction: number
  messagesUnread: number
  sessionChatUnread: number
}

export const EMPTY_ADVISORS_ATTENTION: AdvisorsAttention = {
  agreementsWaitingOnYou: 0,
  boardInvites: 0,
  unreviewedApplications: 0,
  sessionsNeedingAction: 0,
  messagesUnread: 0,
  sessionChatUnread: 0,
}

const toCount = (value: unknown): number => {
  const n = Math.floor(Number(value))
  return Number.isFinite(n) && n > 0 ? n : 0
}

/** Coerces the GraphQL payload (nullable for guests/others) into safe non-negative integers. */
export const toAdvisorsAttention = (
  raw: Partial<Record<keyof AdvisorsAttention, unknown>> | null | undefined
): AdvisorsAttention => ({
  agreementsWaitingOnYou: toCount(raw?.agreementsWaitingOnYou),
  boardInvites: toCount(raw?.boardInvites),
  unreviewedApplications: toCount(raw?.unreviewedApplications),
  sessionsNeedingAction: toCount(raw?.sessionsNeedingAction),
  messagesUnread: toCount(raw?.messagesUnread),
  sessionChatUnread: toCount(raw?.sessionChatUnread),
})

/** Per-parent numbers for the `AdvisorsNav` left nav. */
export type AdvisorsNavAttention = {
  agreements: number
  opportunities: number
  myAdvisors: number
  sessions: number
  messages: number
}

export const advisorsNavAttention = (attention: AdvisorsAttention): AdvisorsNavAttention => ({
  agreements: attention.agreementsWaitingOnYou,
  opportunities: attention.boardInvites,
  myAdvisors: attention.unreviewedApplications,
  // Scheduling items plus session-chat comments (cases 5 + 8).
  sessions: attention.sessionsNeedingAction + attention.sessionChatUnread,
  messages: attention.messagesUnread,
})

/** The site-nav Advisors number: every Advisors leaf summed (L21). */
export const advisorsAttentionTotal = (attention: AdvisorsAttention): number =>
  attention.agreementsWaitingOnYou +
  attention.boardInvites +
  attention.unreviewedApplications +
  attention.sessionsNeedingAction +
  attention.messagesUnread +
  attention.sessionChatUnread

export const ATTENTION_BADGE_CAP = 99

/** Badge text: `null` at zero (hide), `99+` past the cap. */
export const formatAttentionCount = (count: number | null | undefined): string | null => {
  const n = toCount(count)
  if (n === 0) {
    return null
  }
  return n > ATTENTION_BADGE_CAP ? `${ATTENTION_BADGE_CAP}+` : String(n)
}

/**
 * `badgeProps` for a your-turn count (red `count` variant), or `undefined` at zero.
 *
 * The explicit sizing undoes `SidebarNestedNavList`'s `size: 'sm'` (`minH: 7`) so the pill matches
 * the Chat icon and Messages treatment. The visible number is the badge's accessible text.
 */
export const attentionBadgeProps = (count: number | null | undefined): BadgeProps | undefined => {
  const label = formatAttentionCount(count)
  if (!label) {
    return undefined
  }
  return {
    children: label,
    fontSize: '11px',
    lineHeight: '1',
    minH: '1em',
    variant: 'count',
  }
}
